import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconBell, IconChecks } from "@tabler/icons-react";
import { cn } from "@/lib/utils/cn";
import { useNotificationsStore } from "../stores/notificationsStore";
import { notificationIcon, notificationIconTint } from "./notification-icons";
import { notificationRoute, useNotificationFormatter } from "./notification-formatters";
import { ConnectionStatusDot } from "./ConnectionStatusDot";
import type { StoredNotification } from "../types";

/** Quantas notificações o painel do sino mostra (a página /notifications lista todas). */
const VISIBLE_COUNT = 10;

export function NotificationBell() {
  const { t, i18n } = useTranslation(["backoffice", "common"]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const notifications = useNotificationsStore((s) => s.notifications);
  const unread = useNotificationsStore((s) => s.unreadCount);
  const markAsRead = useNotificationsStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationsStore((s) => s.markAllAsRead);
  const status = useNotificationsStore((s) => s.connectionStatus);
  const formatNotification = useNotificationFormatter();

  const visible = notifications.slice(0, VISIBLE_COUNT);

  // Fecha o painel ao clicar fora.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });
  function timeAgo(iso: string): string {
    const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
    if (Math.abs(min) < 60) return rtf.format(-min, "minute");
    const hr = Math.round(min / 60);
    if (Math.abs(hr) < 24) return rtf.format(-hr, "hour");
    return rtf.format(-Math.round(hr / 24), "day");
  }

  function openNotification(n: StoredNotification) {
    if (!n.read) void markAsRead(n.id);
    setOpen(false);
    navigate(notificationRoute(n));
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={
          unread > 0
            ? t("backoffice:notifications.bellUnread", { count: unread })
            : t("common:notifications.title")
        }
        className="relative flex h-9 w-9 items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-bg hover:text-ink"
      >
        <IconBell size={19} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[9px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
        <ConnectionStatusDot className="absolute -bottom-0.5 -right-0.5" />
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-50 w-80 overflow-hidden rounded-xl border border-border bg-white shadow-card">
          <div className="flex items-center justify-between border-b border-border-soft px-4 py-2.5">
            <span className="flex flex-col">
              <span className="text-[13px] font-semibold">{t("common:notifications.title")}</span>
              <span className="flex items-center gap-1.5 text-[10.5px] text-ink-soft">
                <ConnectionStatusDot />
                {t(`backoffice:notifications.connection.${status}`)}
              </span>
            </span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => void markAllAsRead()}
                className="flex items-center gap-1 text-[11.5px] font-medium text-brand-600 hover:text-brand-700"
              >
                <IconChecks size={14} /> {t("common:notifications.markAllRead")}
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {visible.length === 0 ? (
              <p className="px-4 py-8 text-center text-[12.5px] text-ink-soft">
                {t("common:notifications.empty")}
              </p>
            ) : (
              visible.map((n) => {
                const text = formatNotification(n);
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => openNotification(n)}
                    className={cn(
                      "flex w-full items-start gap-3 border-b border-border-soft px-4 py-3 text-left transition-colors last:border-0 hover:bg-bg/60",
                      !n.read && "bg-brand-50/40",
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                        notificationIconTint[n.type],
                      )}
                    >
                      {notificationIcon(n.type)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-[12.5px] font-semibold text-ink">{text.title}</span>
                        {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-brand-500" />}
                      </span>
                      {text.body && (
                        <span className="mt-0.5 line-clamp-2 block text-[11.5px] text-ink-muted">
                          {text.body}
                        </span>
                      )}
                      <span className="mt-1 block text-[10.5px] text-ink-soft">{timeAgo(n.receivedAt)}</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
